import { DOCUMENT_SOURCE, RATE_SHEET_CONSTANTS } from '@/config/constants/rates';
import type { Document } from '@/types';
import type { ExtractedRate } from '@/types/document-extraction';

/**
 * Format a 0–1 decimal discount value (the canonical app-wide format) as a
 * display percentage like "10%". Returns null for null/zero/invalid values so
 * callers can use `v-if` to hide the row entirely.
 */
export const formatDiscountPercentage = (value: number | string | null | undefined): string | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const num = Number(value);

    if (!Number.isFinite(num) || num <= 0) {
        return null;
    }

    return `${(num * 100).toFixed(0)}%`;
};

export const getRateSheetStatusVariant = (status: Document['status']) => {
    // Partial: statuses without an entry (e.g. cancelled) fall back below.
    const statusMap: Partial<Record<Document['status'], 'default' | 'secondary' | 'outline' | 'destructive'>> = {
        [RATE_SHEET_CONSTANTS.STATUSES.COMPLETED]: 'default',
        [RATE_SHEET_CONSTANTS.STATUSES.PROCESSING]: 'secondary',
        [RATE_SHEET_CONSTANTS.STATUSES.EXTRACTING]: 'secondary',
        [RATE_SHEET_CONSTANTS.STATUSES.REVIEW_PENDING]: 'outline',
        [RATE_SHEET_CONSTANTS.STATUSES.REVIEWED]: 'default',
        [RATE_SHEET_CONSTANTS.STATUSES.FAILED]: 'destructive',
        [RATE_SHEET_CONSTANTS.STATUSES.PENDING]: 'secondary',
    };

    return statusMap[status] ?? 'secondary';
};

export const rateSheetIsProcessing = (document: Document) => {
    return (
        document.status === RATE_SHEET_CONSTANTS.STATUSES.PROCESSING ||
        document.status === RATE_SHEET_CONSTANTS.STATUSES.PENDING ||
        document.status === RATE_SHEET_CONSTANTS.STATUSES.EXTRACTING
    );
};

/**
 * Queued or actively extracting — the states `DocumentController::cancel()` can
 * still stop. Deleting a sheet in one of these stops the run first, so delete is
 * offered alongside "Cancel extraction" rather than instead of it.
 *
 * Mirrors `Document::isExtractionInFlight()`.
 */
export const rateSheetIsInFlight = (document: { status: string }): boolean => {
    return document.status === RATE_SHEET_CONSTANTS.STATUSES.PENDING || document.status === RATE_SHEET_CONSTANTS.STATUSES.EXTRACTING;
};

/**
 * `processing` is the finalization pass writing the approved draft into the
 * graph; deleting underneath it would race those writes, and the server refuses
 * it. Every other state - including a sheet still extracting - can be deleted.
 */
export const rateSheetIsDeletable = (document: { status: string }): boolean => {
    return document.status !== RATE_SHEET_CONSTANTS.STATUSES.PROCESSING;
};

/** States an extraction run can be stopped from. Mirrors `Document::isExtractionInFlight()`. */
export const rateSheetIsStoppable = (document: { status: string }): boolean =>
    [RATE_SHEET_CONSTANTS.STATUSES.PENDING, RATE_SHEET_CONSTANTS.STATUSES.EXTRACTING].includes(document.status as never);

/**
 * States a sheet can be re-extracted from with a fresh run of the extractor.
 * Mirrors the guard in DocumentController::reExtract, minus `failed`, which the
 * menus already offer as "Reprocess". Re-extracting a sheet in review or
 * already approved discards the current draft, so callers confirm first.
 */
export const rateSheetIsReExtractable = (document: { status: string }): boolean =>
    [RATE_SHEET_CONSTANTS.STATUSES.REVIEW_PENDING, RATE_SHEET_CONSTANTS.STATUSES.COMPLETED, RATE_SHEET_CONSTANTS.STATUSES.CANCELLED].includes(
        document.status as never,
    );

/**
 * Statuses for which an agency-uploaded ("taken over") sheet still appears on
 * the supplier takeover page. Mirrors SupplierTakeoverController::index — once a
 * sheet leaves these states (reviewed/completed/failed) the takeover page no
 * longer lists it, so funnelling there would land on an empty review page.
 */
const TAKEOVER_PENDING_STATUSES: string[] = [
    RATE_SHEET_CONSTANTS.STATUSES.REVIEW_PENDING,
    RATE_SHEET_CONSTANTS.STATUSES.PENDING,
    RATE_SHEET_CONSTANTS.STATUSES.EXTRACTING,
    RATE_SHEET_CONSTANTS.STATUSES.PROCESSING,
];

/**
 * Whether clicking a sheet should funnel a supplier into the post-upgrade
 * takeover flow (review + connect agencies in context) rather than the normal
 * show/review page. Only agency-uploaded sheets still awaiting review qualify;
 * once the supplier finalizes one it's genuinely theirs and routes normally —
 * otherwise the click lands on the empty takeover page (which excludes
 * finalized sheets).
 */
export const shouldFunnelToTakeover = (document: Document, isAgent: boolean): boolean => {
    return !isAgent && document.metadata?.taken_over === true && TAKEOVER_PENDING_STATUSES.includes(document.status);
};

/**
 * Sharing only gives agents PDF access plus a Touri Chat reference — the agent
 * UI already surfaces a "not searchable yet" indicator for in-flight statuses,
 * so suppliers can manage sharing at any point except when extraction has
 * failed outright.
 *
 * Agent own-uploads (`own_upload`) are permanently private — they can never be
 * shared, mirroring the server-side STO confidentiality invariant.
 */
export const rateSheetIsShareable = (document: Document): boolean => {
    return document.status !== RATE_SHEET_CONSTANTS.STATUSES.FAILED && document.source !== DOCUMENT_SOURCE.OWN_UPLOAD;
};

/**
 * Get a stage-aware ETA message for extraction progress.
 *
 * Extraction stages have wildly different durations:
 *   preparing (10%) ~10s, analyzing (20%) ~30-60s,
 *   extracting (40%) ~5-15 min, verifying (70%) ~1-5 min
 *
 * Linear extrapolation is misleading, so we use stage-based messaging instead.
 */
export const estimateExtractionTime = (progress: number, stage: string): { key: string } | null => {
    if (progress >= 100 || stage === 'complete') {
        return { key: 'almost_done' };
    }

    // Early stages — too soon to estimate
    if (stage === 'preparing' || stage === 'analyzing') {
        return { key: 'calculating' };
    }

    // Extracting stage (20%-40%) — this is the long one
    if (stage === 'extracting') {
        return { key: 'extraction_may_take_several_minutes' };
    }

    // Verifying stage (70%) — nearly there
    if (stage === 'verifying') {
        return { key: 'almost_done' };
    }

    return null;
};

/**
 * The next checkpoint the progress bar should animate toward for each stage.
 * The job reports: 10% (preparing), 20% (analyzing), 40% (extracting), 70% (verifying), 100% (complete).
 */
const STAGE_CEILINGS: Record<string, number> = {
    preparing: 20,
    analyzing: 40,
    extracting: 70,
    verifying: 100,
    complete: 100,
};

/**
 * Calculate a simulated (interpolated) progress value that slowly creeps
 * toward the next stage checkpoint. Uses a logarithmic curve so movement
 * is fast at first then slows — never overshooting the ceiling.
 *
 * @param realProgress - The last reported progress from the server (e.g. 40)
 * @param stage - Current extraction stage
 * @param stageEnteredAt - Timestamp (ms) when this stage/progress was first received
 * @param now - Current timestamp (ms), defaults to Date.now()
 * @returns A display progress value between realProgress and the next checkpoint ceiling
 */
export const interpolateProgress = (realProgress: number, stage: string, stageEnteredAt: number, now?: number): number => {
    const ceiling = STAGE_CEILINGS[stage] ?? realProgress;
    const gap = ceiling - realProgress;

    if (gap <= 0) {
        return realProgress;
    }

    const currentMs = now ?? Date.now();
    const elapsedSeconds = Math.max(0, (currentMs - stageEnteredAt) / 1000);

    // Logarithmic curve: fills ~50% of the gap in ~60s, ~75% in ~180s, ~90% in ~600s
    // Formula: gap * (1 - 1 / (1 + elapsed / 60))
    const filled = gap * (1 - 1 / (1 + elapsedSeconds / 60));

    return Math.min(ceiling - 1, Math.round((realProgress + filled) * 10) / 10);
};

/**
 * A room name as a room is told from another: case, outer spaces and runs of
 * inner spaces aside. The rates' rename, the children rules and the server's
 * `ChildPolicyNormalizer::roomKey()` all match rooms by it, so a rate and its
 * room's rule can never disagree about which room is which.
 */
export const roomKey = (name: string | null | undefined): string => (name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * "Rooms" in the rate editor are a client-side grouping of a property's rates
 * by their `room_type` string — there is no first-class room entity. When a
 * user renames a rate's `room_type`, that single rate would otherwise split
 * away into a brand-new room group, leaving the sibling rates that shared the
 * old name behind under a now-stale room. To rename the room as a whole, every
 * sibling rate sharing the previous room_type must follow the rename.
 *
 * Given the property's current rates (still holding the OLD room_type at the
 * moment an update is emitted, since the editor mutates a clone) and the
 * already-updated rate, this returns the sibling rates that need the same
 * rename — each as a full copy with the new room_type applied. Returns an empty
 * array when the room_type didn't change.
 */
export const collectRoomRenameSiblings = (
    currentRates: ExtractedRate[],
    updatedRate: ExtractedRate,
    /** The rates that may follow (a rename's members, fixed at its first keystroke); every rate of the room when absent. */
    members?: ReadonlySet<string | undefined>,
): ExtractedRate[] => {
    const previousRoomType = currentRates.find((rate) => rate.id === updatedRate.id)?.room_type;

    if (previousRoomType === undefined || previousRoomType === updatedRate.room_type) {
        return [];
    }

    const key = roomKey(previousRoomType);

    return currentRates
        .filter(
            (rate) =>
                rate.id !== updatedRate.id &&
                (members === undefined || members.has(rate.id)) &&
                roomKey(rate.room_type) === key &&
                rate.room_type !== updatedRate.room_type,
        )
        .map((rate) => ({ ...rate, room_type: updatedRate.room_type }));
};

/** A season name as the rate card groups it: case and outer spaces ignored; '' when unnamed. */
const seasonNameKey = (name: string | undefined): string => name?.trim().toLowerCase() ?? '';

/** One season range being renamed, from its first keystroke on. */
interface SeasonRename {
    /** The other ranges at the property that shared its name when the rename began. They follow every later keystroke. */
    members: Array<{ rateId: string | undefined; index: number }>;
    /** The name key the members were last given. A member that no longer holds it was changed elsewhere and is left alone. */
    membersKey: string;
    /** The name key the edited range was last given ('' while it is cleared), to tell a live rename from a stale one. */
    editedKey: string;
}

/**
 * Seasons are the same kind of grouping: the property's rate card has one
 * column per season NAME (`seasonKey`), with no season entity behind it. A
 * season renamed in one rate's editor would split that rate into a new column
 * and leave the old one with a gap, so the rename follows to every season at
 * the property that carried the old name, the rate's own other ranges included.
 *
 * The editor emits on every keystroke, so a rename is followed from its first
 * keystroke: the ranges that shared the name THEN are the ones that follow it.
 * That is what keeps a name typed letter by letter from sweeping up an
 * unrelated season it passes through ("High" on the way from "High Season
 * 2027" to "High Peak"), and lets a name cleared and typed again still rename
 * the season rather than split the rate away from it. The tracker starts over
 * when another rate is edited, when a season is added or removed, or when the
 * edited range no longer holds the name it was last given (an undo).
 *
 * A rename is a season whose name changed at the same position in the list.
 * Adding or removing a season renames nothing. Naming an unnamed season stays
 * on this rate, so the property's unnamed seasons are never swept up together,
 * and a cleared name stays on this rate until a new one is typed.
 *
 * The returned function takes the property's stored rates and the updated
 * rate, and returns the updated rate first, then every other rate that needs
 * changing, each as a full copy. Room renames ({@link collectRoomRenameSiblings})
 * are folded in, so one list carries everything the edit touched.
 */
export const createRateRenameTracker = (): ((
    currentRates: ExtractedRate[],
    updatedRate: ExtractedRate,
    /** `newRoomRename`: this edit starts a new room rename even if it continues the last one's name (its undo step was closed). */
    options?: { newRoomRename?: boolean },
) => ExtractedRate[]) => {
    let trackedRateId: string | undefined;
    let renames = new Map<number, SeasonRename>();
    /**
     * The room rename in progress: the rates of the room when it began follow
     * every later keystroke, and no others. Without it a name typed through
     * another room's ("Family Suite" backspaced to "Family" on its way to
     * "Family Room") would sweep that room's rates up into the rename.
     */
    let roomRename: { rateId: string | undefined; members: Set<string | undefined>; lastName: string } | null = null;

    /** Every range at the property named `key`, but the edited one. */
    const rangesNamed = (rates: ExtractedRate[], key: string, editedRateId: string | undefined, editedIndex: number): SeasonRename['members'] =>
        rates.flatMap((rate) =>
            (rate.seasons ?? []).flatMap((season, index) =>
                seasonNameKey(season.name) === key && !(rate.id === editedRateId && index === editedIndex) ? [{ rateId: rate.id, index }] : [],
            ),
        );

    return (currentRates, updatedRate, options = {}) => {
        const targets = new Map<string | undefined, ExtractedRate>([[updatedRate.id, updatedRate]]);
        const previousRoomType = currentRates.find((rate) => rate.id === updatedRate.id)?.room_type;

        if (previousRoomType !== undefined && previousRoomType !== updatedRate.room_type) {
            if (options.newRoomRename || roomRename === null || roomRename.rateId !== updatedRate.id || roomRename.lastName !== previousRoomType) {
                const key = roomKey(previousRoomType);

                roomRename = {
                    rateId: updatedRate.id,
                    members: new Set(
                        currentRates.filter((rate) => rate.id !== updatedRate.id && roomKey(rate.room_type) === key).map((rate) => rate.id),
                    ),
                    lastName: previousRoomType,
                };
            }

            for (const sibling of collectRoomRenameSiblings(currentRates, updatedRate, roomRename.members)) {
                targets.set(sibling.id, sibling);
            }

            roomRename.lastName = updatedRate.room_type ?? '';
        }

        const previousSeasons = currentRates.find((rate) => rate.id === updatedRate.id)?.seasons ?? [];
        const updatedSeasons = updatedRate.seasons ?? [];

        if (updatedRate.id !== trackedRateId || previousSeasons.length !== updatedSeasons.length) {
            trackedRateId = updatedRate.id;
            renames = new Map();
        }

        if (previousSeasons.length !== updatedSeasons.length) {
            return [...targets.values()];
        }

        /** New names by rate id, then range index, each with the name key the range must still hold. */
        const changes = new Map<string | undefined, Map<number, { expectedKey: string; name: string }>>();

        updatedSeasons.forEach((season, index) => {
            const before = seasonNameKey(previousSeasons[index].name);
            const name = season.name?.trim() ?? '';
            const after = name.toLowerCase();

            if (before === after) {
                return;
            }

            let rename = renames.get(index);

            if (!rename || rename.editedKey !== before) {
                rename = { members: before ? rangesNamed(currentRates, before, updatedRate.id, index) : [], membersKey: before, editedKey: before };
                renames.set(index, rename);
            }

            rename.editedKey = after;

            if (!after) {
                return;
            }

            for (const member of rename.members) {
                const byIndex = changes.get(member.rateId) ?? new Map();
                byIndex.set(member.index, { expectedKey: rename.membersKey, name });
                changes.set(member.rateId, byIndex);
            }

            rename.membersKey = after;
        });

        /** The rate with its member ranges renamed, or null when none of them changed. */
        const renamed = (rate: ExtractedRate): ExtractedRate | null => {
            const byIndex = changes.get(rate.id);

            if (!byIndex) {
                return null;
            }

            let changed = false;
            const seasons = (rate.seasons ?? []).map((season, index) => {
                const change = byIndex.get(index);

                if (!change || seasonNameKey(season.name) !== change.expectedKey || season.name === change.name) {
                    return season;
                }

                changed = true;

                return { ...season, name: change.name };
            });

            return changed ? { ...rate, seasons } : null;
        };

        targets.set(updatedRate.id, renamed(updatedRate) ?? updatedRate);

        for (const rate of currentRates) {
            if (rate.id === updatedRate.id) {
                continue;
            }

            const next = renamed(targets.get(rate.id) ?? rate);

            if (next) {
                targets.set(rate.id, next);
            }
        }

        return [...targets.values()];
    };
};

/** One rename on its own, with no keystrokes before it: see {@link createRateRenameTracker}. */
export const collectRateRenameTargets = (currentRates: ExtractedRate[], updatedRate: ExtractedRate): ExtractedRate[] =>
    createRateRenameTracker()(currentRates, updatedRate);

const SERVICE_PROVIDER_FIELDS = [
    'name',
    'description',
    'website',
    'provider_type',
    'currency',
    'valid_from',
    'valid_to',
    'terms_and_conditions',
    'cancellation_policy',
    'children_policy',
    'general_notes',
    'contact_info',
    'existing_service_id',
] as const;

/**
 * Claude sometimes flattens provider fields directly onto service_provider
 * instead of nesting under service_provider.provider. This normalizes
 * the structure so downstream code can always access provider via .provider.
 */
export const normalizeServiceProvider = (sp: Record<string, unknown>): Record<string, unknown> => {
    if (!sp.provider && sp.name) {
        const provider: Record<string, unknown> = {};

        for (const field of SERVICE_PROVIDER_FIELDS) {
            if (sp[field] !== undefined) {
                provider[field] = sp[field];
            }
        }

        return {
            provider,
            categories: sp.categories || [],
            operating_locations: sp.operating_locations || [],
            addons: sp.addons || [],
        };
    }

    return sp;
};
